<template>
  <div class="edit-carrier-notes-input">
    <div v-if="isEditingNotes">
      <InputWrapper
        v-slot="{ ariaProps, inputId }"
        input-id="update-carrier-notes-text-area"
        :label="t('orders.paramNotes', { param: t('carriers.carriers') })"
      >
        <TextArea
          :id="inputId"
          v-model="internalCarrierNotes"
          v-bind="ariaProps"
          autofocus
          auto-resize
          :placeholder="
            t('orders.paramNotes', { param: t('carriers.carriers') })
          "
        />
      </InputWrapper>

      <div class="edit-notes-buttons">
        <RygenButton
          mode="tertiary"
          :label="t('common.cancel')"
          @click="onCarrierNotesCancel"
        />
        <RygenButton
          mode="primary"
          icon="pi pi-save"
          :label="t('common.save')"
          @click="onCarrierNotesSave"
        />
      </div>
    </div>

    <KeyValuePair
      v-else
      :label="t('orders.paramNotes', { param: t('carriers.carriers') })"
      :value="internalCarrierNotes"
      uppercase-label
    >
      <template #value>
        <button class="carrier-notes-preview" @click="onEditNotes">
          <span class="carrier-notes-preview-text">
            {{ internalCarrierNotes ?? '-' }}
          </span>
          <FontAwesomeIcon
            class="carrier-notes-preview-icon"
            :icon="['fas', 'pencil']"
          />
        </button>
      </template>
    </KeyValuePair>
  </div>
</template>

<script lang="ts" setup>
import { toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import TextArea from 'primevue/textarea';

const internalCarrierNotes = defineModel<string | undefined>(
  'internal-carrier-notes',
  {
    type: String,
    default: () => undefined,
  },
);

const isEditingNotes = defineModel('is-editing-notes', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  carrierNotes: {
    type: String,
    default: () => undefined,
  },
});
const { carrierNotes } = toRefs(props);

const { t } = useI18n();

const onEditNotes = () => {
  isEditingNotes.value = true;
};

const onCarrierNotesCancel = () => {
  internalCarrierNotes.value = carrierNotes?.value;
  isEditingNotes.value = false;
};

const onCarrierNotesSave = () => {
  isEditingNotes.value = false;
};
</script>

<style>
.edit-carrier-notes-input {
  border-top: var(--divider);
  padding-top: var(--sp-3);

  .carrier-notes-preview {
    display: flex;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    width: 100%;
    text-align: left;
    font: inherit;
    color: inherit;

    .carrier-notes-preview-text {
      flex-grow: 1;
      white-space: pre-line;
    }

    .carrier-notes-preview-icon {
      opacity: 0;
      transition: opacity 150ms;
    }
  }

  .carrier-notes-preview:hover {
    .carrier-notes-preview-icon {
      opacity: 1;
    }
  }

  .edit-notes-buttons {
    display: flex;
    gap: var(--gap-1);
    margin-top: var(--sp-3);
    justify-content: flex-end;
  }
}
</style>
